import { useState } from 'react';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { cn } from '@/lib/utils';
import { UserPlus, UserMinus, DollarSign, Clock, HeartPulse } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';
import type { RosterPlayer, FreeAgentPlayer } from '@/hooks/useAddDrop';

const positionColors: Record<string, string> = {
  C: 'bg-orange-500/20 text-orange-400 border-orange-500/30',
  '1B': 'bg-green-500/20 text-green-400 border-green-500/30',
  '2B': 'bg-blue-500/20 text-blue-400 border-blue-500/30',
  '3B': 'bg-red-500/20 text-red-400 border-red-500/30',
  SS: 'bg-purple-500/20 text-purple-400 border-purple-500/30',
  LF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  CF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  RF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  OF: 'bg-cyan-500/20 text-cyan-400 border-cyan-500/30',
  DH: 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30',
  SP: 'bg-red-500/20 text-red-400 border-red-500/30',
  RP: 'bg-amber-500/20 text-amber-400 border-amber-500/30',
  P: 'bg-red-500/20 text-red-400 border-red-500/30',
};

export type WaiverMode = 'fcfs' | 'faab_24' | 'faab_48';

interface AddDropDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** The free agent being added (pre-selected from Players page) */
  addPlayer?: FreeAgentPlayer | null;
  /** The roster player being dropped (pre-selected from My Team) */
  dropPlayer?: RosterPlayer | null;
  /** User's full roster for drop selection */
  roster: RosterPlayer[];
  faabRemaining: number;
  processing: boolean;
  /** Instant add/drop (free_agent mode or true free agents) */
  onConfirm: (addPlayer: FreeAgentPlayer, dropPlayer: RosterPlayer, faabBid: number) => Promise<boolean>;
  /** Add to bench without dropping (when active roster has open slots) */
  onAdd?: (addPlayer: FreeAgentPlayer, faabBid: number) => Promise<boolean>;
  /** Add directly to IL without dropping (IL-eligible players with open IL slots) */
  onAddToIL?: (addPlayer: FreeAgentPlayer, faabBid: number) => Promise<boolean>;
  /** Submit a waiver claim (FAAB modes or waiver-wire players) */
  onSubmitClaim?: (playerId: string, faabBid: number, dropPlayerId: string) => Promise<boolean>;
  /** Current waiver mode from league settings */
  waiverMode?: WaiverMode;
  /** Whether the player being added is on the waiver wire (recently dropped) */
  isOnWaiverWire?: boolean;
  /** Waiver expiry time for countdown display */
  waiverExpiresAt?: string | null;
  /** Pre-validate position limits before confirming */
  checkPositionLimit?: (addPlayer: FreeAgentPlayer, dropPlayer: RosterPlayer) => string | null;
  /** Check if added player can play the dropped player's slot */
  checkSlotEligibility?: (addPlayer: FreeAgentPlayer, dropPlayer: RosterPlayer) => string | null;
  /** Whether the add target is IL-eligible */
  isAddPlayerILEligible?: boolean;
  /** Number of open IL slots */
  availableILSlots?: number;
  /** Number of open active roster slots (IL players don't count against active limit) */
  openActiveSlots?: number;
  /** Max active roster size for display */
  totalRosterSize?: number;
}

const AddDropDialog = ({
  open,
  onOpenChange,
  addPlayer: preselectedAdd,
  dropPlayer: preselectedDrop,
  roster,
  faabRemaining,
  processing,
  onConfirm,
  onAdd,
  onAddToIL,
  onSubmitClaim,
  waiverMode = 'fcfs',
  isOnWaiverWire = false,
  waiverExpiresAt,
  checkPositionLimit,
  checkSlotEligibility,
  isAddPlayerILEligible = false,
  availableILSlots = 0,
  openActiveSlots = 0,
  totalRosterSize = 25,
}: AddDropDialogProps) => {
  const [selectedDrop, setSelectedDrop] = useState<RosterPlayer | null>(null);
  const [faabBid, setFaabBid] = useState('0');
  // 'choose' = choosing IL vs active roster, 'il' = confirmed IL placement, 'active' = normal add/drop, 'add' = add without drop
  const [placementMode, setPlacementMode] = useState<'choose' | 'il' | 'active' | 'add'>('choose');

  const dropTarget = preselectedDrop || selectedDrop;
  const addTarget = preselectedAdd;

  // Determine if this transaction should go through waivers
  const isFaabMode = waiverMode === 'faab_24' || waiverMode === 'faab_48';
  const requiresClaim = isOnWaiverWire && isFaabMode;
  const showFaabInput = requiresClaim;

  // Can this player be added directly to IL?
  const canAddToIL = isAddPlayerILEligible && availableILSlots > 0 && !!onAddToIL;

  // Pre-validate position limit when both targets are selected
  const positionLimitError = addTarget && dropTarget && checkPositionLimit
    ? checkPositionLimit(addTarget, dropTarget)
    : null;

  // Check slot eligibility — warning (not blocking) if player goes to BENCH
  const slotEligibilityWarning = addTarget && dropTarget && checkSlotEligibility
    ? checkSlotEligibility(addTarget, dropTarget)
    : null;

  // Can add without dropping (open active roster slot)?
  const hasOpenSlot = openActiveSlots > 0 && !!onAdd;

  const handleConfirm = async () => {
    if (!addTarget) return;

    // IL placement path — no drop needed
    if (placementMode === 'il' && onAddToIL) {
      const success = await onAddToIL(addTarget, parseInt(faabBid) || 0);
      if (success) {
        resetState();
        onOpenChange(false);
      }
      return;
    }

    // Add without drop path (open active roster slot)
    if (isAddMode && onAdd) {
      const success = await onAdd(addTarget, parseInt(faabBid) || 0);
      if (success) {
        resetState();
        onOpenChange(false);
      }
      return;
    }

    // Normal add/drop path
    if (!dropTarget) return;

    if (requiresClaim && onSubmitClaim) {
      const success = await onSubmitClaim(addTarget.id, parseInt(faabBid) || 0, dropTarget.player_id);
      if (success) {
        resetState();
        onOpenChange(false);
      }
    } else {
      const success = await onConfirm(addTarget, dropTarget, parseInt(faabBid) || 0);
      if (success) {
        resetState();
        onOpenChange(false);
      }
    }
  };

  const resetState = () => {
    setSelectedDrop(null);
    setFaabBid('0');
    setPlacementMode('choose');
  };

  const handleClose = (isOpen: boolean) => {
    if (!isOpen) resetState();
    onOpenChange(isOpen);
  };

  // Flow states
  // Show choice screen when IL is an option (user chooses IL vs bench/active roster)
  const hasPlacementOptions = canAddToIL && !preselectedDrop;
  const showPlacementChoice = addTarget && hasPlacementOptions && placementMode === 'choose';
  const isILMode = placementMode === 'il';
  // Auto-advance to 'add' mode when open slot exists, not IL-eligible, and no preselected drop
  const isAddMode = placementMode === 'add' || (hasOpenSlot && !canAddToIL && !preselectedDrop && placementMode === 'choose');
  const needsDropSelection = !isILMode && !isAddMode && addTarget && !dropTarget && !showPlacementChoice;
  const readyToConfirm = addTarget && (isILMode || isAddMode || dropTarget);

  const claimLabel = 'FAAB Bid';
  const getCountdownText = () => {
    if (!waiverExpiresAt) return '';
    const expiresAt = new Date(waiverExpiresAt);
    const now = new Date();
    const diffMs = expiresAt.getTime() - now.getTime();
    if (diffMs <= 0) return 'Waiver window expired';
    const hours = Math.floor(diffMs / (1000 * 60 * 60));
    const minutes = Math.floor((diffMs % (1000 * 60 * 60)) / (1000 * 60));
    return `Waiver clears in ${hours}h ${minutes}m`;
  };
  const processingSchedule = requiresClaim ? getCountdownText() : '';

  // Format the IL status for display
  const formatILStatus = (status?: string) => {
    if (!status) return 'IL';
    return status
      .replace(/^Injured (\d+)-Day$/, 'IL$1')
      .replace(/^Injured - Full Season$/, 'IL-FS')
      .replace(/^(\d+)-Day Injured List$/, 'IL$1');
  };

  const title = showPlacementChoice
    ? `Add ${addTarget?.full_name}`
    : readyToConfirm
    ? isILMode
      ? `Add ${addTarget?.full_name} to IL`
      : isAddMode
      ? `Add ${addTarget?.full_name}`
      : requiresClaim ? `Submit ${claimLabel}` : 'Confirm Add/Drop'
    : needsDropSelection
    ? `Add ${addTarget?.full_name}`
    : preselectedDrop
    ? `Drop ${preselectedDrop.full_name}`
    : 'Add/Drop Player';

  const description = showPlacementChoice
    ? canAddToIL
      ? 'This player is IL-eligible. Choose where to place them.'
      : `You have ${openActiveSlots} open roster spot${openActiveSlots !== 1 ? 's' : ''}. No drop required.`
    : readyToConfirm
    ? isILMode
      ? `Place on Injured List (${availableILSlots} IL slot${availableILSlots !== 1 ? 's' : ''} available). No drop required.`
      : isAddMode
      ? `No drop required (${openActiveSlots} open roster spot${openActiveSlots !== 1 ? 's' : ''}).`
      : requiresClaim
        ? showFaabInput
          ? 'Set your FAAB bid amount to claim this player.'
          : 'Submit a waiver claim for this player.'
        : 'Confirm the transaction below.'
    : needsDropSelection
    ? 'Select a player from your roster to drop.'
    : 'Complete the transaction below.';

  return (
    <Dialog open={open} onOpenChange={handleClose}>
      <DialogContent className="max-w-lg max-h-[80vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>{title}</DialogTitle>
          <DialogDescription>{description}</DialogDescription>
        </DialogHeader>

        {/* Roster count indicator */}
        {(() => {
          const activeCount = totalRosterSize - openActiveSlots;
          return (
            <div className="flex items-center justify-between text-xs text-muted-foreground px-1">
              <span>Roster: {activeCount}/{totalRosterSize}</span>
              {openActiveSlots <= 0 && (
                <span className="text-amber-400 font-medium">Full: drop required</span>
              )}
            </div>
          );
        })()}

        {showPlacementChoice ? (
          /* Step: Choose placement option */
          <div className="space-y-3">
            <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
              <div className="flex items-center gap-2">
                <UserPlus className="w-4 h-4 text-primary" />
                <span className="text-sm font-medium">{addTarget!.full_name}</span>
                <Badge variant="outline" className={cn("text-[9px] px-1 h-4", positionColors[addTarget!.position_abbreviation])}>
                  {addTarget!.position_abbreviation}
                </Badge>
                {isAddPlayerILEligible && (
                  <Badge variant="outline" className="text-[9px] px-1 h-4 bg-red-500/20 text-red-400 border-red-500/30 ml-auto">
                    {formatILStatus(addTarget!.player_status)}
                  </Badge>
                )}
              </div>
            </div>

            {hasOpenSlot && (
              <button
                onClick={() => setPlacementMode('add')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left"
              >
                <UserPlus className="w-5 h-5 text-primary shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Add</p>
                  <p className="text-xs text-muted-foreground">
                    No drop required ({openActiveSlots} open roster spot{openActiveSlots !== 1 ? 's' : ''})
                  </p>
                </div>
              </button>
            )}

            {canAddToIL && (
              <button
                onClick={() => setPlacementMode('il')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left"
              >
                <HeartPulse className="w-5 h-5 text-red-400 shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Add to Injured List</p>
                  <p className="text-xs text-muted-foreground">
                    No drop required ({availableILSlots} IL slot{availableILSlots !== 1 ? 's' : ''} open)
                  </p>
                </div>
              </button>
            )}

            {!hasOpenSlot && (
              <button
                onClick={() => setPlacementMode('active')}
                className="w-full flex items-center gap-3 px-4 py-3 rounded-lg border border-border hover:border-primary/50 hover:bg-primary/5 transition-colors text-left"
              >
                <UserPlus className="w-5 h-5 text-primary shrink-0" />
                <div className="flex-1">
                  <p className="text-sm font-medium text-foreground">Add to Active Roster</p>
                  <p className="text-xs text-muted-foreground">
                    Requires dropping a player
                  </p>
                </div>
              </button>
            )}
          </div>
        ) : readyToConfirm ? (
          <div className="space-y-4">
            {isILMode ? (
              /* IL placement confirmation — no drop card */
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-center gap-2">
                  <HeartPulse className="w-4 h-4 text-red-400" />
                  <span className="text-sm font-medium">Add to IL: {addTarget!.full_name}</span>
                  <Badge variant="outline" className={cn("text-[9px] px-1 h-4 ml-auto", positionColors[addTarget!.position_abbreviation])}>
                    {addTarget!.position_abbreviation}
                  </Badge>
                </div>
              </div>
            ) : isAddMode ? (
              /* Add without drop confirmation */
              <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                <div className="flex items-center gap-2">
                  <UserPlus className="w-4 h-4 text-primary" />
                  <span className="text-sm font-medium">Add: {addTarget!.full_name}</span>
                  <Badge variant="outline" className={cn("text-[9px] px-1 h-4 ml-auto", positionColors[addTarget!.position_abbreviation])}>
                    {addTarget!.position_abbreviation}
                  </Badge>
                </div>
              </div>
            ) : (
              /* Normal add/drop confirmation */
              <>
                <div className="p-3 rounded-lg bg-destructive/10 border border-destructive/20">
                  <div className="flex items-center gap-2">
                    <UserMinus className="w-4 h-4 text-destructive" />
                    <span className="text-sm font-medium">Drop: {dropTarget!.full_name}</span>
                    <Badge variant="outline" className={cn("text-[9px] px-1 h-4 ml-auto", positionColors[dropTarget!.position_abbreviation])}>
                      {dropTarget!.position_abbreviation}
                    </Badge>
                  </div>
                </div>
                <div className="p-3 rounded-lg bg-primary/10 border border-primary/20">
                  <div className="flex items-center gap-2">
                    <UserPlus className="w-4 h-4 text-primary" />
                    <span className="text-sm font-medium">Add: {addTarget!.full_name}</span>
                    <Badge variant="outline" className={cn("text-[9px] px-1 h-4 ml-auto", positionColors[addTarget!.position_abbreviation])}>
                      {addTarget!.position_abbreviation}
                    </Badge>
                  </div>
                </div>
              </>
            )}

            {showFaabInput && (
              <div className="space-y-2">
                <label className="text-sm font-medium text-foreground flex items-center gap-1.5">
                  <DollarSign className="w-4 h-4" />
                  FAAB Bid
                </label>
                <div className="flex items-center gap-2">
                  <span className="text-sm font-bold text-muted-foreground">$</span>
                  <Input
                    type="number"
                    min={0}
                    max={faabRemaining}
                    value={faabBid}
                    onChange={(e) => setFaabBid(e.target.value)}
                    className="w-24"
                  />
                  <span className="text-xs text-muted-foreground">
                    of ${faabRemaining} remaining
                  </span>
                </div>
              </div>
            )}

            {requiresClaim && processingSchedule && (
              <div className="flex items-center gap-2 text-xs text-muted-foreground bg-muted/30 rounded-lg px-3 py-2">
                <Clock className="w-3.5 h-3.5 shrink-0" />
                <span>{processingSchedule}</span>
              </div>
            )}

            {!isILMode && !isAddMode && positionLimitError && (
              <div className="flex items-center gap-2 text-xs text-destructive bg-destructive/10 rounded-lg px-3 py-2">
                <span>{positionLimitError}</span>
              </div>
            )}

            {!isILMode && !isAddMode && !positionLimitError && slotEligibilityWarning && (
              <div className="flex items-center gap-2 text-xs text-amber-400 bg-amber-500/10 rounded-lg px-3 py-2">
                <span>{slotEligibilityWarning}</span>
              </div>
            )}

            <div className="flex gap-2 pt-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => {
                  if (isILMode || isAddMode) {
                    setPlacementMode(hasPlacementOptions ? 'choose' : 'active');
                  } else if (!preselectedDrop) {
                    setSelectedDrop(null);
                  } else {
                    handleClose(false);
                  }
                }}
              >
                Back
              </Button>
              <Button
                className="flex-1"
                onClick={handleConfirm}
                disabled={processing || (!isILMode && !isAddMode && !!positionLimitError)}
              >
                {processing
                  ? 'Processing...'
                  : isILMode
                  ? 'Add to IL'
                  : isAddMode
                  ? 'Add'
                  : requiresClaim
                  ? `Submit Claim${showFaabInput && parseInt(faabBid) > 0 ? ` ($${faabBid})` : ''}`
                  : `Confirm${parseInt(faabBid) > 0 ? ` ($${faabBid})` : ''}`}
              </Button>
            </div>
          </div>
        ) : needsDropSelection ? (
          <div className="space-y-2">
            <p className="text-sm font-medium text-muted-foreground">Select player to drop:</p>
            {roster.map(p => (
              <button
                key={p.assignment_id}
                onClick={() => setSelectedDrop(p)}
                className="w-full flex items-center gap-3 px-3 py-2 rounded-lg border border-border hover:border-destructive/50 hover:bg-destructive/5 transition-colors text-left"
              >
                {p.headshot_url ? (
                  <img src={p.headshot_url} alt="" className="w-7 h-7 rounded-full bg-muted object-cover" />
                ) : (
                  <div className="w-7 h-7 rounded-full bg-muted flex items-center justify-center">
                    <span className="text-[9px] font-bold text-muted-foreground">
                      {p.full_name.split(' ').map(n => n[0]).join('')}
                    </span>
                  </div>
                )}
                <Badge variant="outline" className={cn("text-[10px] px-1.5 h-5", positionColors[p.position_abbreviation])}>
                  {p.position_abbreviation}
                </Badge>
                <span className="text-sm font-medium text-foreground">{p.full_name}</span>
                <span className="text-xs text-muted-foreground ml-auto">{p.slot === 'BENCH' ? 'BN' : p.slot.replace(/\d+$/, '')}</span>
              </button>
            ))}
          </div>
        ) : null}
      </DialogContent>
    </Dialog>
  );
};

export default AddDropDialog;
